<template>
  <div>
    <input
      v-model.trim="search"
      ref="search"
      type="text"
      :placeholder="$t('tradeIn.searchProductList')"
      class="form-control"
    />
    <div class="ts-mt-3 ts-border ts-border-gray-300 ts-p-3 ts-rounded">
      <ts-loading-banner :loading="loading">
        <div class="max-h">
          <table class="ts-w-full">
            <thead>
              <tr>
                <th style="width: 10%;">
                  {{ $t('tradeIn.barcode') }}
                </th>
                <th style="width: 13%;">
                  {{ $t('tradeIn.productGroup') }}
                </th>
                <th style="width: 24%;">
                  {{ $t('tradeIn.productName') }}
                </th>
                <th style="width: 10%;">
                  {{ $t('tradeIn.uom') }}
                </th>
                <th>
                  {{ $t('tradeIn.serailList') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(product, index) in invoiceProducts" :key="index">
                <td>{{ product.barcode }}</td>
                <td>{{ product.pro_group_name_en }}</td>
                <td>{{ product.product_name_en }}</td>
                <td>
                  {{ product.uom_name_en }}
                </td>
                <td class="ts-text-right">
                  <div
                    v-for="(serial, index) in product.serial_list"
                    :key="index"
                  >
                  <div class="alert alert-primary ts-flex ts-items-center ts-justify-center ts-p-1 ts-rounded">
                    <ul class="text-left">
                      <li>{{ $t('tradeIn.serialNo') }}: {{ serial.serial_no + ' - ' + serial.product_grade }}</li>
                      <li>{{ $t('tradeIn.vinImei') }}: {{ serial.imei ? serial.imei : 'N/A' }}</li>
                      <li>{{ $t('tradeIn.plateNumber') }}:{{ serial.plate_number ? serial.plate_number : 'N/A' }}</li>
                    </ul>
                    <div class="ts-flex-grow"></div>
                    <a href="#" class="btn btn-sm btn-primary" @click.prevent="addItem(product, serial)">
                      {{ $t('add') }}</a>
                  </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </ts-loading-banner>
    </div>
    <small>
      <i>{{ $t('tradeIn.clickToAddProduct') }}</i>
    </small>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'productList',
  props: ['value', 'loading'],
  data() {
    return {
      search: '',
      model: {}
    }
  },
  created() {
    this.model = this.value
  },
  computed: {
    ...mapState('stock/tradeIn', ['invoice_products']),
    invoiceProducts() {
      if (!this.invoice_products) return []

      const existingSerials = this.model.product_details.flatMap(item =>
        item.serial_list.map(s => s.serial_no)
      )

      return this.invoice_products
        .map(product => {
          // Filter out serials that already exist
          const availableSerials = product.serial_list.filter(
            s => !existingSerials.includes(s.serial_no)
          )

          // Return a copy of product with only available serials
          return {
            ...product,
            serial_list: availableSerials
          }
        })
        .filter(product => {
          // Only include if there's at least one available serial
          if (product.serial_list.length === 0) return false

          const searchTerms = this.search.toLowerCase().split(' ')
          const searchableFields = [
            product.barcode,
            product.product_name_en,
            ...product.serial_list.map(s => s.serial_no || ''),
            ...product.serial_list.map(s => s.imei || ''),
            ...product.serial_list.map(s => s.plate_number || ''),
          ].map(f => (f || '').toLowerCase())

          return searchTerms.every(term =>
            searchableFields.some(f => f.includes(term))
          )
        })
    }
  },
  methods: {
    addItem(product, serial) {
      // Find if the item already exists based on product_id and uom_id
      const existingItem = this.model.product_details.find(
        item =>
          item.product_id === product.product_id &&
          item.uom_id === product.uom_id
      )

      if (existingItem) {
        // Check if the serial already exists
        const isSerialExist = existingItem.serial_list.some(
          s => s.serial_no === serial.serial_no
        )
        if (!isSerialExist) {
          existingItem.serial_list.push(serial)
          existingItem.quantity = existingItem.serial_list.length
        }
      } else {
        // Create a new item if it doesn't exist
        const newItem = {
          product_id: product.product_id,
          barcode: product.barcode,
          product_name_en: product.product_name_en,
          product_name_kh: product.product_name_kh,
          uom_id: product.uom_id,
          uom_name_en: product.uom_name_en,
          uom_name_kh: product.uom_name_kh,
          pro_group_name_en: product.pro_group_name_en,
          pro_group_name_kh: product.pro_group_name_kh,
          cost: product.cost,
          input_cost: product.cost,
          currency_id: product.currency_id,
          fx_rate: product.fx_rate,
          fx_rate_ratio: product.fx_rate_ratio,
          input_currency_id: product.input_currency_id,
          is_generate_unique_serial: product.is_generate_unique_serial,
          serial_list: [serial],
          quantity: 1,
          input_total_amount: 0,
          total_amount: 0
        }
        this.model.product_details.push(newItem)
      }
    }
  }
}
</script>

<style scoped>
.max-h {
  min-height: 60vh;
  max-height: 60vh;
  overflow-y: scroll;
}

.form-control {
  height: 32px;
}

table {
  width: 100%;
}

table thead tr {
  border-width: 1px;
  z-index: 3;
  position: sticky;
  top: 0;
  /* Don't forget this, required for the stickiness */
}

table thead tr th {
  white-space: nowrap;
  background-color: #edf2f7;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
}

table tbody tr {
  cursor: pointer;
  border-width: 1px;
}

table tbody tr:hover {
  background-color: #e2e8f0;
}

table tbody tr td {
  white-space: nowrap;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
  border-right-width: 1px;
}
</style>
